// @ts-check

import { swanAccountStatus } from '@/core/constants'
import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useStore } from 'vuex'

export const useCompanyStore = defineStore('company', () => {
  /** @type {import('vuex').Store<RootState>} */
  const store = useStore()

  const company = computed(() => store.state.companyData.company)

  const isActive = computed(() => company.value?.status?.status === 'active')

  const isNotActive = computed(() => !isActive.value)

  const companyId = computed(() => store.state.companyData.companyId)

  const isSwanAccountClosingOrClosed = computed(() => {
    const swanStatus = store.state.companyData.company.swan_status
    return (
      swanStatus === swanAccountStatus.CLOSED ||
      swanStatus === swanAccountStatus.CLOSING
    )
  })

  const isSwanAccountPending = computed(() => {
    const swanStatus = store.state.companyData.company.swan_status
    return swanStatus === swanAccountStatus.PENDING
  })

  const isSwanAccountPendingOrConnected = computed(() => {
    const swanStatus = store.state.companyData.company.swan_status
    return (
      swanStatus === swanAccountStatus.PENDING ||
      swanStatus === swanAccountStatus.CONNECTED
    )
  })

  const isSwanAccountVerificationNotStarted = computed(() => {
    const swanAccountVerificationStatus =
      store.state.companyData.company.swan_verification_status
    return swanAccountVerificationStatus === 'not_started'
  })

  const purchaseInvoiceEmail = computed(
    () => store.state.companyData.purchaseInvoiceEmail
  )

  const purchaseInvoiceFinvoice = computed(
    () => store.state.companyData.purchaseInvoiceFinvoice
  )

  const isFoundingNewBusiness = computed(
    () => company.value?.is_company_founding ?? false
  )

  const hasBusinessId = computed(() => !!company.value?.business_id)

  const hasSignedPoa = computed(() => company.value?.is_signed ?? false)

  const isCourier = computed(
    () => company.value?.courier_info?.is_partner_courier ?? false
  )

  /** @param {Partial<CompanyResource>} companyData */
  const setCompany = (companyData) => {
    store.commit('companyData/setCompanyData', companyData)
  }

  /** @param {Partial<CompanyResource>} companyData */
  const updateCompany = (companyData) => {
    store.commit('companyData/updateCompanyData', companyData)
  }

  /** @param {Partial<UserResource>} userData */
  const updateCompanyUser = async (userData) => {
    await store.dispatch('companyData/setUserCompanyDetails', userData)
  }

  const getUserCompany = async () => {
    await store.dispatch('companyData/getUserCompany')
  }

  return {
    company,
    isActive,
    isNotActive,
    isSwanAccountClosingOrClosed,
    isSwanAccountPending,
    isSwanAccountPendingOrConnected,
    isSwanAccountVerificationNotStarted,
    setCompany,
    updateCompany,
    updateCompanyUser,
    getUserCompany,
    companyId,
    purchaseInvoiceEmail,
    purchaseInvoiceFinvoice,
    isFoundingNewBusiness,
    hasBusinessId,
    hasSignedPoa,
    isCourier,
    $reset: () => true
  }
})

export default useCompanyStore
